import { useQueryClient } from '@tanstack/react-query';
import { LANGUAGE_AUTONYMS } from '../i18n';
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Footer, HeaderWeb, SkipLink } from '../components/idsk';
import i18n, { changeLanguage, currentLanguage, isLanguage } from '../i18n';
import { logout } from '../auth/keycloak';
import { useAppInfo, useMe } from '../api/me';
import { useRouteFocus } from './RouteFocus';
import { SessionWarning } from './SessionWarning';
import { useStickyHeaderOffset } from './useStickyHeaderOffset';
import { useAnnouncer } from './Announcer';
import { Toaster } from '../components/common/Toaster';

export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { announce } = useAnnouncer();
  const me = useMe().data;
  const info = useAppInfo().data;
  useRouteFocus();
  useStickyHeaderOffset();

  const lang = currentLanguage();
  const admin = me?.admin ?? false;
  const nav = [
    { to: '/inspekcie', text: t('nav.inspections') },
    { to: '/subjekty', text: t('nav.subjects') },
    ...(admin
      ? [
          { to: '/inspektori', text: t('nav.inspectors') },
          { to: '/sablony', text: t('nav.templates') },
          { to: '/audit', text: t('nav.audit') },
        ]
      : []),
  ].map((item) => ({ ...item, active: location.pathname === item.to || location.pathname.startsWith(`${item.to}/`) }));

  const onLanguageChange = async (code: string) => {
    if (!isLanguage(code) || code === lang) return;
    await changeLanguage(code);
    await queryClient.invalidateQueries();
    announce(i18n.t('header.languageChanged', { lng: code }));
    requestAnimationFrame(() => document.querySelector<HTMLElement>('.idsk-header-web__brand-language-button')?.focus());
  };

  return (
    <>
      <SkipLink text={t('layout.skipLink')} />
      <HeaderWeb
        key={`${admin ? 'admin' : 'user'}-${lang}`}
        title={t('app.name')}
        languages={[
          { code: 'sk', label: LANGUAGE_AUTONYMS.sk, selected: lang === 'sk' },
          { code: 'en', label: LANGUAGE_AUTONYMS.en, selected: lang === 'en' },
        ]}
        onLanguageChange={(code) => void onLanguageChange(code)}
        navigation={nav}
        userName={me?.displayName}
        onLogout={() => void logout()}
      />
      <div className="govuk-width-container">
        <main className="govuk-main-wrapper app-main" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <Footer
        visuallyHiddenTitle={t('footer.supportLinks')}
        links={[{ to: '/pristupnost', text: t('footer.accessibility') }]}
        meta={<span data-testid="app-version">{t('footer.version', { version: info?.version ?? '…' })}</span>}
        copy={t('footer.copy')}
      />
      <Toaster />
      <SessionWarning />
    </>
  );
}
